import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { csrfFetch, clearCsrfToken } from '../utils/csrfFetch';
import { BOOTSTRAP_MODEL_ID } from '../hooks/useModelCatalog';

/**
 * UserContext - Manages authentication state, org context, and RBAC (DOS-25, DOS-213)
 *
 * Role hierarchy: superadmin > org_admin > location_admin > manager > user
 * Plus per-user persona (nullable) that layers additional skill/data capabilities.
 */

const ROLE_RANK = { superadmin: 5, org_admin: 4, group_admin: 4, location_admin: 3, manager: 2, user: 1 };

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);
  const [orgs, setOrgs] = useState([]);
  // Sample dealers' orgs any signed-in user may enter (skills plan step 4a).
  const [sampleOrgs, setSampleOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  // initialAuthLoading is TRUE only during the first checkAuth() call on mount,
  // and FALSE forever after. AuthGate uses this (NOT `loading`) to decide whether
  // to show PageLoader. Reason: `loading` flips to true on every login/register/
  // logout call, which would unmount LoginPage mid-submit and lose the catch
  // handler's setMode('reset-password') etc. Splitting the flags keeps LoginPage
  // mounted during interactive auth flows so its local state survives. See TODO #34.
  const [initialAuthLoading, setInitialAuthLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-user default model (settable only in Settings → Chat), or null when
  // the user has no preference. Null on purpose: callers then fall back to
  // the CATALOG's default (useModelCatalog), which a superadmin sets at
  // /admin/models — a hardcoded fallback here would shadow it for every user
  // who never opened Settings (which was all of them, 2026-10-06).
  const [defaultModel, setDefaultModelState] = useState(null);
  // DOS-213: capability-based permissions + persona
  const [capabilities, setCapabilities] = useState(new Set());
  const [persona, setPersona] = useState(null);

  useEffect(() => {
    // Initial checkAuth — clears initialAuthLoading once finished (success or
    // failure). Subsequent checkAuth calls (e.g., post-login refresh) do not
    // re-trigger this flag, so AuthGate doesn't unmount LoginPage mid-flow.
    checkAuth().finally(() => setInitialAuthLoading(false));

    // Global 401 interceptor — when any API call returns 401 (session expired),
    // clear user state so AuthGate shows the login page instead of a broken UI.
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (response.status === 401 && url.includes('/api/') && !url.includes('/api/auth/login') && !url.includes('/api/auth/config')) {
        setUser(prev => {
          if (prev) sessionStorage.setItem('sessionExpired', 'true');
          return null;
        });
        setOrg(null);
        setRole(null);
        setOrgs([]);
        setSampleOrgs([]);
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  // DOS-213: Resolve capability set from backend. Silently fails — hasRole fallback covers it.
  // Declared before checkAuth() so the latter can reference it as a useCallback
  // dep without hitting JS's temporal-dead-zone.
  const loadCapabilities = useCallback(async () => {
    try {
      const r = await csrfFetch('/api/personas/me/capabilities', { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      setCapabilities(new Set(data.capabilities || []));
      setPersona(data.persona || null);
    } catch (err) {
      // Non-fatal — UI degrades to role-based checks
      console.warn('capability load failed', err);
    }
  }, []);

  // checkAuth is the canonical session-hydration path: it hits /api/auth/me,
  // which returns the FULL session shape (user, org, role, orgs, sampleOrgs,
  // defaultModel via preferences,
  // and capabilities via loadCapabilities). The login(), register() (auto-
  // verified branch), and the initial mount effect ALL call checkAuth() so
  // there's a single source of truth for "what's on the session." Pre-fix,
  // login() and register() each set a *subset* of these fields manually,
  // which left session fields (then including demo state) and defaultModel
  // stale until a manual refresh. See TODO #50.
  //
  // useCallback so login()/register() can list it in their deps without
  // re-memoizing on every render. Deps: loadCapabilities (itself stable).
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await csrfFetch('/api/auth/me', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setOrg(data.org || null);
        setRole(data.role || null);
        setOrgs(data.orgs || []);
        setSampleOrgs(data.sampleOrgs || []);
        // Per-user model preference, or null → the catalog default applies.
        setDefaultModelState(data.user?.preferences?.defaultModel || null);
        // Await caps so callers (login/register) can rely on a fully-hydrated
        // session before their loading flag flips false — eliminates the
        // first-paint flash where capability-based AdminGuards rendered as
        // hidden until caps resolved.
        await loadCapabilities();
      } else {
        setUser(null);
        setOrg(null);
        setRole(null);
        setOrgs([]);
        setSampleOrgs([]);
        setDefaultModelState(null);
        setCapabilities(new Set());
        setPersona(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setOrg(null);
      setRole(null);
      setOrgs([]);
      setSampleOrgs([]);
      setCapabilities(new Set());
      setPersona(null);
    } finally {
      setLoading(false);
    }
  }, [loadCapabilities]);

  const login = useCallback(async (identifier, password = null, name = null) => {
    try {
      setLoading(true);
      setError(null);

      // Send both `username` AND `email` with the same value. The Phase 3
      // backend tries username first, then falls back to email_normalized.
      // This lets users type either their username or their email into
      // the single login field without us guessing client-side.
      const response = await csrfFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: identifier,
          email: identifier,
          password,
          name,
        })
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.error === 'EmailNotVerified') {
          // Throw a typed error so LoginPage can switch into check-email mode.
          setLoading(false);
          const err = new Error('EmailNotVerified');
          err.code = 'EmailNotVerified';
          err.email = data.email || identifier;
          throw err;
        }
        if (data.error === 'TempPasswordLogin') {
          // Throw a typed error so LoginPage can switch into reset-password mode.
          // Belt + suspenders: set both .message and .code so the consumer can
          // match on either. The generic fall-through below sets only .message
          // and empirically doesn't trigger the LoginPage catch — bypass it.
          setLoading(false);
          const err = new Error('TempPasswordLogin');
          err.code = 'TempPasswordLogin';
          err.email = data.email || identifier;
          throw err;
        }
        // Phase 3 signup_status gate — surface dedicated codes so the
        // frontend can render appropriate holding/error screens instead
        // of the generic "Login failed" toast.
        if (
          data.error === 'PendingApproval' ||
          data.error === 'AccountSuspended' ||
          data.error === 'AccountRejected'
        ) {
          setLoading(false);
          const err = new Error(data.error);
          err.code = data.error;
          err.message = data.message || data.error;
          throw err;
        }
        const errMsg = data.message || data.error || 'Login failed';
        setError(errMsg);
        setLoading(false);
        throw new Error(errMsg);
      }

      const data = await response.json();
      // Server-side regenerates session ID on successful login (session-fixation
      // defense at server/routes/auth.js:721). The cached CSRF token was bound
      // to the OLD session id; drop it so the next state-changing call mints a
      // fresh one. csrfFetch also self-heals on 403 "invalid csrf token" — this
      // is the belt; that's the suspenders.
      clearCsrfToken();
      // RCA follow-up (2026-06-09): delegate to checkAuth() instead of
      // hydrating user/org/role/orgs/capabilities by hand. The /api/auth/login
      // response is intentionally minimal — it omits sampleOrgs and
      // defaultModel-from-preferences. The
      // initial workaround (await loadCapabilities) only patched one missing
      // field; switching to checkAuth() makes /api/auth/me the single source
      // of truth for the session shape and eliminates the whole class of
      // "field-was-missing-until-refresh" bugs. Costs one extra GET per login.
      await checkAuth();
      return data.user;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  const register = useCallback(async (email, password, name = null, kyc = {}, bot = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await csrfFetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          name,
          company: kyc.company || '',
          business_purpose: kyc.businessPurpose || '',
          // Phase 2 bot-check fields. Empty when Turnstile isn't configured;
          // the backend's TURNSTILE_SECRET_KEY feature-flag accepts that.
          'cf-turnstile-response': bot.turnstileToken || '',
          company_url: bot.honeypot || '',
        })
      });

      if (!response.ok && response.status !== 202) {
        const data = await response.json();
        throw new Error(data.message || 'Registration failed');
      }

      const data = await response.json();
      // 202 = account created but unverified. Do NOT set user/session state —
      // caller (LoginPage) will switch into the "check your email" mode.
      if (data.verified === false || response.status === 202) {
        return { verified: false, email: data.email || email };
      }
      // Legacy/auto-verified responses still hydrate the session. Same
      // CSRF-clear + checkAuth-delegation pattern as login() — see there.
      clearCsrfToken();
      await checkAuth();
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      const res = await csrfFetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      const { logoutUrl } = await res.json().catch(() => ({}));
      setUser(null);
      setOrg(null);
      setRole(null);
      setOrgs([]);
      setSampleOrgs([]);
      setCapabilities(new Set());
      setPersona(null);
      // Server destroys the session on logout — the cached CSRF token is now
      // bound to a dead session id. Wipe it so the next mint targets the new
      // anonymous session.
      clearCsrfToken();
      // Clear per-session client state so it never bleeds into the next login
      localStorage.removeItem('decisiv-performance-unread');
      // Signed in through the Cognito user pool: end its sign-in cookie too,
      // or "Sign in" would go straight back in. It returns to the app.
      if (logoutUrl) window.location.assign(logoutUrl);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    try {
      setError(null);
      const response = await csrfFetch('/api/auth/switch-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ org_id: orgId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to switch organization');
      }

      const data = await response.json();
      setOrg(data.org);
      setRole(data.role);
      // Capabilities can change per-org (e.g. user is org_admin in org A but
      // only `user` in org B). Hydrate before returning so the caller's
      // post-switch render sees the correct admin set immediately.
      await loadCapabilities();
      return data.org;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [loadCapabilities]);

  const updateProfile = useCallback(async (updates) => {
    try {
      setLoading(true);
      setError(null);

      const response = await csrfFetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Update failed');
      }

      const data = await response.json();
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Role helper: check if current role meets or exceeds requiredRole
  const hasRole = useCallback((requiredRole) => {
    if (!role) return false;
    return (ROLE_RANK[role] || 0) >= (ROLE_RANK[requiredRole] || 0);
  }, [role]);

  const isAdmin = useCallback(() => hasRole('org_admin'), [hasRole]);
  const isManager = useCallback(() => hasRole('manager'), [hasRole]);
  const isSuperAdmin = useCallback(() => role === 'superadmin', [role]);

  // DOS-213: Capability-based helpers. Prefer these over hasRole() for new code.
  const hasCapability = useCallback((cap) => capabilities.has(cap), [capabilities]);
  const hasAnyCapability = useCallback((...caps) => caps.some(c => capabilities.has(c)), [capabilities]);

  // Persist the user's default chat model. The model is the only place this
  // value is settable -- there is no inline switcher in the chat composer.
  const setDefaultModel = useCallback(async (modelId) => {
    if (!modelId || modelId === defaultModel) return;
    // Optimistic update so the Settings UI feels instant.
    setDefaultModelState(modelId);
    try {
      await csrfFetch('/api/auth/profile', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { defaultModel: modelId } }),
      });
    } catch { /* network blip — UI already reflects the new value */ }
  }, [defaultModel]);

  const value = {
    user,
    org,
    role,
    orgs,
    sampleOrgs,
    loading,
    initialAuthLoading,
    error,
    defaultModel,
    setDefaultModel,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    switchOrg,
    updateProfile,
    checkAuth,
    hasRole,
    isAdmin,
    isManager,
    isSuperAdmin,
    // DOS-213
    capabilities,
    persona,
    hasCapability,
    hasAnyCapability,
    refreshCapabilities: loadCapabilities,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
